import React, { useEffect, useState } from "react";
import type { User } from "../shared/contracts.ts";
import { api, Banner, Button, Icon } from "./ui.tsx";
import type { CalendarOccurrence } from "../shared/calendar.ts";
import { PersonalProgressCard } from "./progress.tsx";
import { FeaturedGoalCard } from "./goals.tsx";
import type { VisitsSummary } from "../shared/visits.ts";
export function Home({
  user,
  demandAccess,
  visitsAccess,
  visitsIndicators,
  calendarAccess,
  go,
  onAcceptance,
  onOpenDemand,
}: {
  user: User;
  demandAccess: boolean;
  visitsAccess: boolean;
  visitsIndicators: boolean;
  calendarAccess: boolean;
  go: (page: string) => void;
  onAcceptance: () => void;
  onOpenDemand: (id: string) => void;
}) {
  const [counts, setCounts] = useState<{
    active: number;
    waiting: number;
  } | null>(null);
  const [visitsSummary, setVisitsSummary] = useState<VisitsSummary | null>(
    null,
  );
  const [visitsError, setVisitsError] = useState(false);
  const [focusDemands, setFocusDemands] = useState<
    {
      id: string;
      citizen: { name: string };
      status: string;
      priority: string;
      deadline: string | null;
    }[]
  >([]);
  const [today, setToday] = useState<CalendarOccurrence[] | null>(null),
    [countError, setCountError] = useState(""),
    [agendaError, setAgendaError] = useState(""),
    [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    setCounts(null);
    setFocusDemands([]);
    setToday(null);
    setCountError("");
    setAgendaError("");
    setVisitsSummary(null);
    setVisitsError(false);
    if (visitsAccess && visitsIndicators)
      api<VisitsSummary>(
        "/visits-overview/summary",
        "GET",
        undefined,
        abort.signal,
      )
        .then((value) => {
          if (!abort.signal.aborted) setVisitsSummary(value);
        })
        .catch((e) => {
          if (e.name !== "AbortError") setVisitsError(true);
        });
    if (demandAccess)
      Promise.all([
        api<any>(
          `/demands?area=active&limit=3&sort=priority&assessor=${encodeURIComponent(user.id)}`,
          "GET",
          undefined,
          abort.signal,
        ),
        api<any>(
          `/demands?area=awaiting_acceptance&limit=1&assessor=${encodeURIComponent(user.id)}`,
          "GET",
          undefined,
          abort.signal,
        ),
      ])
        .then(([a, b]) => {
          if (!abort.signal.aborted) {
            setCounts({ active: a.total, waiting: b.total });
            setFocusDemands(a.rows);
          }
        })
        .catch((e) => {
          if (e.name !== "AbortError") setCountError(e.message);
        });
    if (calendarAccess) {
      const day = new Intl.DateTimeFormat("sv-SE", {
        timeZone: "America/Sao_Paulo",
      }).format(new Date());
      api<CalendarOccurrence[]>(
        `/calendar/appointments?ownerId=${encodeURIComponent(user.id)}&from=${day}&to=${day}`,
        "GET",
        undefined,
        abort.signal,
      )
        .then((items) => {
          if (!abort.signal.aborted) setToday(items);
        })
        .catch((e) => {
          if (e.name !== "AbortError") setAgendaError(e.message);
        });
    }
    return () => abort.abort();
  }, [
    user.id,
    demandAccess,
    calendarAccess,
    visitsAccess,
    visitsIndicators,
    refresh,
  ]);
  return (
    <section className="system-home">
      <header className="home-heading">
        <div className="home-desktop-greeting">
          <span className="home-eyebrow">SEU DIA, COM CLAREZA</span>
          <h1>Olá, {user.name.split(" ")[0]}.</h1>
          <p>O que precisa de atenção e o caminho que você já percorreu.</p>
        </div>
        <div className="home-mobile-greeting">
          <h1>O que você precisa fazer?</h1>
          <p>Seu trabalho, um passo de cada vez.</p>
        </div>
        <time className="home-current-date" dateTime={new Date().toISOString()}>
          {new Intl.DateTimeFormat("pt-BR", {
            timeZone: "America/Sao_Paulo",
            day: "numeric",
            month: "long",
          }).format(new Date())}
          <span>
            {new Intl.DateTimeFormat("pt-BR", {
              timeZone: "America/Sao_Paulo",
              weekday: "long",
              year: "numeric",
            }).format(new Date())}
          </span>
        </time>
      </header>
      <div className="home-desktop-highlights">
        <FeaturedGoalCard go={go} />
        <PersonalProgressCard go={go} />
      </div>
      <div className="home-desktop-stats" aria-label="Resumo do trabalho">
        {demandAccess && (
          <Button onClick={() => go("demands")}>
            <span>
              Demandas em andamento
              <Icon name="document" />
            </span>
            <strong>
              {countError
                ? "Indisponível"
                : counts === null
                  ? "Carregando…"
                  : counts.active}
            </strong>
            <small>Com você · abrir lista</small>
          </Button>
        )}
        {visitsAccess && (
          <>
            <Button onClick={() => go("visits-overview")}>
              <span>
                Visitas pendentes
                <Icon name="search" />
              </span>
              <strong>
                {!visitsIndicators
                  ? "Consultar"
                  : visitsError
                    ? "Indisponível"
                    : visitsSummary
                      ? (visitsSummary.metrics.pending ?? "—")
                      : "Carregando…"}
              </strong>
              <small>Ver o escopo e os dados disponíveis</small>
            </Button>
            <Button onClick={() => go("visits-overview")}>
              <span>
                Visitas realizadas
                <Icon name="check" />
              </span>
              <strong>
                {!visitsIndicators
                  ? "Consultar"
                  : visitsError
                    ? "Indisponível"
                    : visitsSummary
                      ? (visitsSummary.metrics.visited ?? "—")
                      : "Carregando…"}
              </strong>
              <small>Ver endereços e cobertura</small>
            </Button>
          </>
        )}
        {calendarAccess && (
          <Button onClick={() => go("calendar")}>
            <span>
              Compromissos do dia
              <Icon name="calendar" />
            </span>
            <strong>
              {agendaError
                ? "Indisponível"
                : today === null
                  ? "Carregando…"
                  : today.length}
            </strong>
            <small>Sua agenda de hoje</small>
          </Button>
        )}
      </div>
      {visitsAccess && visitsIndicators && (
        <p className="home-visits-context">
          Visitas: consulta consolidada das fontes autorizadas.{" "}
          {visitsError
            ? "Não foi possível atualizar os indicadores; abra a consulta para tentar novamente."
            : visitsSummary
              ? {
                  ready:
                    "Confira a atualização e a composição ao abrir o indicador.",
                  partial: "Base parcial: há fontes indisponíveis.",
                  stale: "Dados desatualizados.",
                  unavailable:
                    "Dados ainda indisponíveis; ausência de cálculo não significa zero.",
                  updating: "Atualização em andamento.",
                }[visitsSummary.freshness.state]
              : "Carregando indicadores…"}{" "}
          {visitsSummary?.metrics.provisional ? "Contagem provisória." : ""}
        </p>
      )}
      {countError && (
        <Banner kind="warn">
          Não foi possível atualizar suas pendências. {countError}
          <Button onClick={() => setRefresh((n) => n + 1)}>
            Tentar novamente
          </Button>
        </Banner>
      )}
      <div className="home-actions" aria-label="Suas áreas de trabalho">
        {demandAccess && (
          <Button onClick={() => go("demands")}>
            <Icon name="document" />
            <span className="home-action-copy">
              <strong>Demandas</strong>
              <span>
                {countError
                  ? "Abrir suas demandas"
                  : counts === null
                    ? "Carregando…"
                    : `${counts.active} em andamento com você`}
              </span>
            </span>
            <Icon name="arrow" size={17} />
          </Button>
        )}
        {visitsAccess && (
          <Button onClick={() => go("visits-overview")}>
            <Icon name="search" />
            <span className="home-action-copy">
              <strong>Visão geral das visitas</strong>
              <span>Endereços e cobertura</span>
            </span>
            <Icon name="arrow" size={17} />
          </Button>
        )}
      </div>
      {!!counts?.waiting && (
        <Button className="demand-acceptance" onClick={onAcceptance}>
          {counts.waiting}{" "}
          {counts.waiting === 1
            ? "demanda aguardando seu aceite"
            : "demandas aguardando seu aceite"}
        </Button>
      )}
      <div className="home-detail-columns">
        {demandAccess && (
          <section className="home-focus">
            <div className="actions">
              <div>
                <h2>Demandas em foco</h2>
                <p>Da sua fila, por prioridade</p>
              </div>
              <Button kind="ghost" onClick={() => go("demands")}>
                Ver demandas
              </Button>
            </div>
            {countError ? (
              <p>Não foi possível consultar sua fila.</p>
            ) : counts === null ? (
              <p role="status">Carregando demandas…</p>
            ) : focusDemands.length ? (
              <ul>
                {focusDemands.map((d) => (
                  <li key={d.id}>
                    <Button onClick={() => onOpenDemand(d.id)}>
                      <Icon name="document" />
                      <span>
                        <strong>{d.citizen.name}</strong>
                        <small>
                          {d.status} ·{" "}
                          {d.deadline
                            ? new Date(
                                d.deadline + "T12:00:00Z",
                              ).toLocaleDateString("pt-BR", {
                                timeZone: "UTC",
                                day: "numeric",
                                month: "short",
                              })
                            : "Sem prazo definido"}
                        </small>
                      </span>
                      <span className="home-priority">{d.priority}</span>
                      <Icon name="arrow" size={16} />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Nenhuma demanda em andamento com você.</p>
            )}
          </section>
        )}
        {calendarAccess && (
          <section className="home-today">
            <div className="actions">
              <h2>Hoje</h2>
              <Button kind="ghost" onClick={() => go("calendar")}>
                Ver agenda
              </Button>
            </div>
            {agendaError ? (
              <Banner kind="warn">
                Não foi possível consultar sua agenda.{" "}
                <Button onClick={() => setRefresh((n) => n + 1)}>
                  Tentar novamente
                </Button>
              </Banner>
            ) : today === null ? (
              <p>Carregando agenda…</p>
            ) : today.length ? (
              <ul>
                {today.slice(0, 3).map((a) => (
                  <li key={a.seriesId + a.originalDate}>
                    <span className="home-agenda-time">
                      {a.allDay ? "Dia inteiro" : a.startTime}
                    </span>
                    <strong>{a.title}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <p>Nenhum compromisso na sua agenda hoje.</p>
            )}
          </section>
        )}
      </div>
      {!demandAccess && !visitsAccess && !calendarAccess && (
        <p>O administrador ainda precisa liberar suas áreas de trabalho.</p>
      )}
      <details className="system-scope">
        <summary>O que já está disponível nesta etapa?</summary>
        <p>
          Demandas e calendário salvam no banco do servidor. Ofícios permanece
          no computador. Visão geral das visitas utiliza as fontes publicadas e
          informa quando a integração é simulada. Perfil, saldos de Pontos/XP,
          Rankings e Metas de Demandas usam registros do servidor e as
          configurações publicadas pelo administrador.
        </p>
        <p>
          Cadastro e registro operacional de visitas, mapas, Saúde, gamificação
          completa, fila offline, notificações push, relatórios gerais e backups
          automáticos ainda precisam de implementação ou integração. As telas da
          proposta visual não confirmam essas funções.
        </p>
        <p>
          A disponibilidade de Google e IA depende da configuração e validação
          de cada integração. O envio automático ao Solar ainda está em
          desenvolvimento. As funções indisponíveis não são simuladas nesta tela.
        </p>
      </details>
    </section>
  );
}
